import React, { useEffect, useState } from 'react';
import { 
  Check, 
  Copy, 
  KeyRound, 
  Plus, 
  RefreshCw, 
  ShieldCheck, 
  Trash2, 
  Database, 
  Archive, 
  FileText,
  Clock,
  Network,
  Terminal
} from 'lucide-react';
import { api } from '../services/api';
import { Agent, ApplicationIntegration, RetentionPolicy } from '../types';
import { AppPermissionMap } from './AppPermissionMap';
import { SiemQueryExporter } from './SiemQueryExporter';

interface IntegrationsProps {
  setActiveTab: (tab: string) => void;
  role: 'owner' | 'admin' | 'operator' | 'viewer';
}

export const Integrations: React.FC<IntegrationsProps> = ({ role }) => {
  const [activeView, setActiveView] = useState<'map' | 'siem' | 'keys'>('map');
  const [applications, setApplications] = useState<ApplicationIntegration[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [retention, setRetention] = useState<RetentionPolicy | null>(null);
  const [archives, setArchives] = useState<Array<{ id: string; format: string; record_count: number; integrity_hash: string; created_at: string }>>([]);
  const [name, setName] = useState('My AI application');
  const [description, setDescription] = useState('Centralized safety and audit telemetry for an AI-built application.');
  const [environment, setEnvironment] = useState('production');
  const [agentId, setAgentId] = useState('');
  const [newKey, setNewKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const canManageApplications = role !== 'viewer';
  const canManageRetention = role === 'owner' || role === 'admin';

  const load = async () => {
    setError(null);
    try {
      const [apps, registeredAgents, policy, archList] = await Promise.all([
        api.getIntegrations(), 
        api.getAgents(), 
        api.getRetention(),
        api.getArchives().catch(() => [])
      ]);
      setApplications(apps);
      setAgents(registeredAgents.filter((agent) => !agent.archived_at));
      setRetention(policy);
      setArchives(archList);
      if (!agentId && registeredAgents[0]) setAgentId(registeredAgents[0].id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Integration settings are unavailable.');
    }
  };

  useEffect(() => { load(); }, []);

  const copy = async (value: string) => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  const create = async () => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const result = await api.createIntegration({ name, description, environment, agent_id: agentId || undefined });
      setNewKey(result.integration_key);
      setMessage('Integration created. Copy the key now; it will not be shown again.');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to create integration.');
    } finally {
      setBusy(false);
    }
  };

  const rotate = async (id: string) => {
    setBusy(true);
    setError(null);
    try {
      const result = await api.rotateIntegration(id);
      setNewKey(result.integration_key);
      setMessage('The previous key was revoked and a new key was issued.');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to rotate integration key.');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id: string) => {
    setBusy(true);
    setError(null);
    try {
      await api.revokeIntegration(id);
      setMessage('Integration key revoked.');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to revoke integration key.');
    } finally {
      setBusy(false);
    }
  };

  const saveRetention = async () => {
    if (!retention) return;
    setBusy(true);
    setError(null);
    try {
      const next = await api.updateRetention({ 
        hot_retention_days: retention.hot_retention_days, 
        archive_enabled: Boolean(retention.archive_enabled), 
        legal_hold: Boolean(retention.legal_hold) 
      });
      setRetention(next);
      setMessage('Retention policy updated.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to update retention policy.');
    } finally {
      setBusy(false);
    }
  };

  const prune = async () => {
    if (!window.confirm('Prune audit events past the retention window?')) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.pruneRetention();
      setMessage(`Pruning complete. ${result.deleted} events removed.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Telemetry prune failed.');
    } finally {
      setBusy(false);
    }
  };

  const triggerR2Archive = async () => {
    setArchiving(true);
    setError(null);
    setMessage(null);
    try {
      const res = await api.archiveToR2();
      setMessage(`Successfully exported ${res.record_count} audit records to Cloudflare R2 cold bucket (${res.export_id}). SHA-256: ${res.integrity_hash.slice(0, 16)}…`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export cold archive to R2.');
    } finally {
      setArchiving(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-8 animate-fade-in">
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white sm:text-3xl">Agent Integrations & Evidence Storage</h1>
        <p className="mt-1.5 max-w-3xl text-sm text-slate-600 dark:text-slate-400">Connect AI-built applications to a centralized safety, audit, trace, and evidence layer. Keys are scoped to your organization.</p>
      </div>

      {error && <div role="alert" className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs text-rose-800 dark:border-rose-900/50 dark:bg-rose-950/20 dark:text-rose-300">{error}</div>}
      {message && <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-xs text-emerald-800 dark:border-emerald-900/50 dark:bg-emerald-950/20 dark:text-emerald-300">{message}</div>}
      
      {/* View Segmented Switcher */}
      <div className="flex flex-wrap items-center gap-2 p-1.5 rounded-2xl bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 w-fit">
        <button
          type="button"
          onClick={() => setActiveView('map')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition cursor-pointer ${
            activeView === 'map'
              ? 'bg-white dark:bg-slate-800 text-brand-600 dark:text-brand-400 shadow-sm'
              : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
          }`}
        >
          <Network className="w-4 h-4" />
          <span>Application Permission Topology</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveView('siem')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition cursor-pointer ${
            activeView === 'siem'
              ? 'bg-white dark:bg-slate-800 text-brand-600 dark:text-brand-400 shadow-sm'
              : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
          }`}
        >
          <Terminal className="w-4 h-4 text-brand-500" />
          <span>SIEM Queries & NIST SP 800-53</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveView('keys')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition cursor-pointer ${
            activeView === 'keys'
              ? 'bg-white dark:bg-slate-800 text-brand-600 dark:text-brand-400 shadow-sm'
              : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
          }`}
        >
          <KeyRound className="w-4 h-4" />
          <span>API Keys & R2 Cold Storage</span>
        </button>
      </div>

      {activeView === 'map' ? (
        <AppPermissionMap />
      ) : activeView === 'siem' ? (
        <SiemQueryExporter />
      ) : (
        <div className="space-y-8 animate-fade-in">
          <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
        <section className="glass-panel space-y-5 rounded-2xl p-6">
          <div className="flex items-center gap-2"><Plus className="h-4 w-4 text-brand-600 dark:text-brand-400" /><h2 className="text-lg font-bold text-slate-900 dark:text-white">Register an application</h2></div>
          <p className="text-xs leading-5 text-slate-500 dark:text-slate-400">Use one integration per application or environment. The generated key can call telemetry and gateway endpoints only within its configured scope.</p>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="text-xs font-semibold text-slate-600 dark:text-slate-400">Application name<input value={name} onChange={(event) => setName(event.target.value)} disabled={!canManageApplications} className="mt-1.5 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-white" /></label>
            <label className="text-xs font-semibold text-slate-600 dark:text-slate-400">Environment<select value={environment} onChange={(event) => setEnvironment(event.target.value)} disabled={!canManageApplications} className="mt-1.5 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-white"><option>production</option><option>staging</option><option>development</option></select></label>
          </div>
          <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400">Primary agent profile<select value={agentId} onChange={(event) => setAgentId(event.target.value)} disabled={!canManageApplications} className="mt-1.5 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-white"><option value="">No fixed agent (application-scoped telemetry)</option>{agents.map((agent) => <option key={agent.id} value={agent.id}>{agent.name} · {agent.id}</option>)}</select></label>
          <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400">Description<textarea value={description} onChange={(event) => setDescription(event.target.value)} disabled={!canManageApplications} rows={3} className="mt-1.5 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-white" /></label>
          {canManageApplications ? <button type="button" onClick={create} disabled={busy || !name.trim()} className="inline-flex items-center gap-2 rounded-xl bg-brand-600 px-4 py-2.5 text-xs font-bold text-white shadow-sm hover:bg-brand-500 disabled:opacity-50"><KeyRound className="h-4 w-4" />{busy ? 'Creating…' : 'Create integration key'}</button> : <p className="text-xs text-slate-500 dark:text-slate-400">Ask an application owner or operator to register this application.</p>}
          {newKey && <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 dark:border-amber-800/60 dark:bg-amber-950/20"><div className="flex items-center justify-between gap-3"><span className="text-[10px] font-bold uppercase tracking-[0.16em] text-amber-800 dark:text-amber-300">Copy this key now</span><button type="button" onClick={() => copy(newKey)} className="inline-flex items-center gap-1 rounded-lg border border-amber-300 px-2 py-1 text-[11px] font-bold text-amber-800 dark:border-amber-700 dark:text-amber-300">{copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />} {copied ? 'Copied' : 'Copy'}</button></div><code className="mt-3 block break-all rounded-lg bg-white/70 p-3 font-mono text-xs text-slate-900 dark:bg-slate-950/60 dark:text-slate-100">{newKey}</code></div>}
        </section>

        <section className="glass-panel space-y-5 rounded-2xl p-6">
          <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-600 dark:text-emerald-400" /><h2 className="text-lg font-bold text-slate-900 dark:text-white">MCP connection</h2></div>
          <p className="text-xs leading-5 text-slate-500 dark:text-slate-400">Connect an MCP client to the public endpoint for read-only demo tools, or include an organization integration key for scoped telemetry and evaluation.</p>
          <pre className="overflow-x-auto rounded-xl border border-slate-800 bg-slate-950 p-4 text-[11px] leading-5 text-slate-100">{`{\n  "mcpServers": {\n    "agenticscale": {\n      "url": "https://agenticscale.org/api/mcp",\n      "headers": {\n        "X-AgenticScale-Key": "ags_…"\n      }\n    }\n  }\n}`}</pre>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-xs leading-5 text-slate-600 dark:border-slate-800 dark:bg-slate-950/50 dark:text-slate-400"><strong className="text-slate-900 dark:text-slate-100">Available tools:</strong> start a run, evaluate an action, log a redacted event, complete a run, read an agent profile, and retrieve run evidence.</div>
        </section>
      </div>

      <section className="glass-panel space-y-4 rounded-2xl p-6">
        <div className="flex items-center justify-between gap-3"><div><h2 className="text-lg font-bold text-slate-900 dark:text-white">Registered applications</h2><p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Active keys are shown only by prefix. Rotate when an application changes owners or environments.</p></div><button type="button" onClick={load} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"><RefreshCw className="h-3.5 w-3.5" />Refresh</button></div>
        {applications.length === 0 ? <p className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-xs text-slate-500 dark:border-slate-700">No applications registered yet.</p> : <div className="space-y-3">{applications.map((application) => <div key={application.id} className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/40 sm:flex-row sm:items-center sm:justify-between"><div><div className="font-bold text-slate-900 dark:text-white">{application.name}</div><div className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">{application.environment} · {application.agent_names || 'application-scoped'} · {application.key_count || 0} active key(s)</div></div>{canManageApplications && <div className="flex flex-wrap gap-2"><button type="button" onClick={() => rotate(application.id)} disabled={busy} className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"><RefreshCw className="h-3 w-3" />Rotate</button><button type="button" onClick={() => revoke(application.id)} disabled={busy} className="inline-flex items-center gap-1 rounded-lg border border-rose-200 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-50 dark:border-rose-900/50 dark:bg-slate-900 dark:text-rose-300"><Trash2 className="h-3 w-3" />Revoke</button></div>}</div>)}</div>}
      </section>

      {/* Retention Controls & R2 Cold Storage */}
      <section className="glass-panel space-y-6 rounded-2xl p-6">
        <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
          <div>
            <h2 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <Database className="h-5 w-5 text-brand-600 dark:text-brand-400" />
              <span>Retention & Cloudflare R2 Cold Storage</span>
            </h2>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Control hot D1 retention and export immutable historical evidence bundles into Cloudflare R2 bucket storage.</p>
          </div>
          {canManageRetention && (
            <button
              type="button"
              onClick={triggerR2Archive}
              disabled={archiving}
              className="inline-flex items-center gap-1.5 rounded-xl bg-brand-600 hover:bg-brand-500 text-white px-3.5 py-2 text-xs font-bold shadow-sm transition disabled:opacity-50 shrink-0"
            >
              <Archive className="h-4 w-4" />
              <span>{archiving ? 'Exporting to R2…' : 'Archive to Cloudflare R2'}</span>
            </button>
          )}
        </div>

        {retention && (
          <div className="grid gap-4 sm:grid-cols-3">
            <label className="text-xs font-semibold text-slate-600 dark:text-slate-400">
              Hot retention days
              <input 
                type="number" 
                min={7} 
                max={3650} 
                value={retention.hot_retention_days} 
                onChange={(event) => setRetention({ ...retention, hot_retention_days: Number(event.target.value) })} 
                disabled={!canManageRetention} 
                className="mt-1.5 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-white" 
              />
            </label>
            <label className="flex items-center gap-2 pt-6 text-xs font-semibold text-slate-600 dark:text-slate-400">
              <input 
                type="checkbox" 
                checked={Boolean(retention.archive_enabled)} 
                onChange={(event) => setRetention({ ...retention, archive_enabled: event.target.checked ? 1 : 0 })} 
                disabled={!canManageRetention} 
              />
              Archive export enabled
            </label>
            <label className="flex items-center gap-2 pt-6 text-xs font-semibold text-slate-600 dark:text-slate-400">
              <input 
                type="checkbox" 
                checked={Boolean(retention.legal_hold)} 
                onChange={(event) => setRetention({ ...retention, legal_hold: event.target.checked ? 1 : 0 })} 
                disabled={!canManageRetention} 
              />
              Legal hold active
            </label>
          </div>
        )}

        {canManageRetention ? (
          <div className="flex flex-wrap gap-2 pt-1 border-t border-slate-100 dark:border-slate-800">
            <button type="button" onClick={saveRetention} disabled={busy || !retention} className="rounded-xl bg-brand-600 px-4 py-2 text-xs font-bold text-white hover:bg-brand-500 disabled:opacity-50">Save retention policy</button>
            <button type="button" onClick={prune} disabled={busy || Boolean(retention?.legal_hold)} className="rounded-xl border border-rose-200 px-4 py-2 text-xs font-bold text-rose-700 hover:bg-rose-50 disabled:opacity-50 dark:border-rose-900/50 dark:text-rose-300 dark:hover:bg-rose-950/20">Prune old telemetry</button>
          </div>
        ) : (
          <p className="text-xs text-slate-500 dark:text-slate-400">Retention changes and pruning are restricted to AI administrators and workspace owners.</p>
        )}

        {/* Historical R2 Archives Table */}
        <div className="space-y-3 pt-2">
          <div className="flex items-center gap-1.5 text-xs font-bold text-slate-900 dark:text-white">
            <Clock className="h-4 w-4 text-slate-500" />
            <span>Historical Evidence Archives in R2 Cold Storage</span>
          </div>
          {archives.length === 0 ? (
            <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-950/50 border border-slate-200 dark:border-slate-800 text-xs text-slate-500">
              No R2 archive exports created yet. Click "Archive to Cloudflare R2" to generate an immutable cold backup.
            </div>
          ) : (
            <div className="divide-y divide-slate-100 dark:divide-slate-800 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden bg-slate-50 dark:bg-slate-950/50 text-xs">
              {archives.slice(0, 5).map((arch) => (
                <div key={arch.id} className="p-3.5 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div className="space-y-0.5">
                    <div className="font-mono font-bold text-slate-900 dark:text-white flex items-center gap-2">
                      <FileText className="h-3.5 w-3.5 text-brand-600 dark:text-brand-400" />
                      <span>{arch.id}</span>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-sans font-semibold uppercase">{arch.format}</span>
                    </div>
                    <div className="text-[11px] text-slate-500 dark:text-slate-400">
                      {arch.record_count} records bundled · SHA-256: <code className="font-mono">{arch.integrity_hash?.slice(0, 16)}…</code>
                    </div>
                  </div>
                  <div className="text-[11px] text-slate-500 dark:text-slate-400 font-mono shrink-0">
                    {new Date(arch.created_at).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
      </div>
      )}
    </div>
  );
};
